import * as React from "react"
import { cn } from "@/lib/utils"

export interface TagLabelProps extends React.HTMLAttributes<HTMLSpanElement> {
  /**
   * Optional icon to display on the left side of the text
   */
  leftIcon?: React.ReactNode
  /**
   * Optional icon to display on the right side of the text
   */
  rightIcon?: React.ReactNode
  /**
   * Text color class (defaults to gray)
   * This color will be applied to text, border, and icons
   * The border color automatically matches the text color exactly
   * Can be any Tailwind text color class (e.g., "text-blue-600", "text-green-500")
   * For dark mode, include both: "text-blue-600 dark:text-blue-400"
   */
  textColor?: string
  /**
   * Border color class (optional override)
   * By default, border color automatically matches textColor exactly
   * Only use this if you need a different border color than the text
   * Can be any Tailwind border color class
   */
  borderColor?: string
}

function TagLabel({
  className,
  leftIcon,
  rightIcon,
  textColor = "text-gray-600 dark:text-gray-400",
  borderColor,
  children,
  ...props
}: TagLabelProps) {
  // Derive border color from text color - use lighter shades for borders
  // This ensures borders are lighter than the text color for better visual hierarchy
  const getBorderColor = () => {
    if (borderColor) return borderColor

    // Convert text color to lighter border color
    // For light mode: maps to lighter shades (300)
    // For dark mode: maps gray colors to gray-600, others to 300
    // Handles both light and dark mode: "text-red-600 dark:text-red-400" -> "border-red-300 dark:border-red-300"
    // For gray in dark mode: "text-gray-600 dark:text-gray-400" -> "border-gray-300 dark:border-gray-600"
    return textColor
      .replace(/dark:text-gray-(\d+)/g, "dark:border-gray-600")
      .replace(/text-(\w+)-600/g, "border-$1-300")
      .replace(/text-(\w+)-700/g, "border-$1-300")
      .replace(/text-(\w+)-500/g, "border-$1-300")
      .replace(/text-(\w+)-400/g, "border-$1-300")
      .replace(/text-(\w+)-300/g, "border-$1-300")
      .replace(/text-(\w+)-800/g, "border-$1-300")
      .replace(/text-(\w+)-900/g, "border-$1-300")
  }

  const borderColorClass = getBorderColor()
  // Icons use the same color as text
  const iconColorClass = textColor

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium bg-transparent",
        textColor,
        borderColorClass,
        className
      )}
      {...props}
    >
      {leftIcon && <span className={cn("shrink-0", iconColorClass)}>{leftIcon}</span>}
      <span>{children}</span>
      {rightIcon && <span className={cn("shrink-0", iconColorClass)}>{rightIcon}</span>}
    </span>
  )
}

export { TagLabel }
